import React from 'react'
import type { Block } from '../../editor/types'
import { Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'

interface TextBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
}

type TextStyle = 'paragraph' | 'h1' | 'h2' | 'h3' | 'bold' | 'list-bullet' | 'list-ordered' | 'quote' | 'highlight' | 'callout-info' | 'callout-warning' | 'callout-success' | 'callout-error'

function interpolate(
  text: string,
  variableValues: Record<string, number | string | boolean>,
  accent: string
) {
  return text.split(/(@\w+)/g).map((part, i) => {
    const match = part.match(/^@(\w+)$/)
    if (match) {
      const varName = match[1]
      const value = variableValues[varName]
      return (
        <span
          key={i}
          className="font-mono font-medium px-1.5 py-0.5 rounded"
          style={{
            color: accent,
            backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)`,
          }}
        >
          {value !== undefined ? String(value) : part}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

export default function TextBlock({ block, variableValues }: TextBlockProps) {
  const content = block.content || ''
  const accent = block.accent || 'var(--accent, #5048e5)'
  const config = (block.config || {}) as Record<string, unknown>
  const style = (config.style as TextStyle) || 'paragraph'
  const align = config.align as string | undefined

  const baseStyle = {
    color: block.accent ? accent : '#475569',
    textAlign: (align as 'left' | 'center' | 'right') || 'left',
  } as const

  if (style === 'list-bullet' || style === 'list-ordered') {
    const lines = content.split('\n').filter((line) => line.trim() !== '')
    const Tag = style === 'list-bullet' ? 'ul' : 'ol'
    const listClass = style === 'list-bullet' ? 'list-disc pl-5 space-y-1' : 'list-decimal pl-5 space-y-1'
    return (
      <Tag className={listClass} style={baseStyle}>
        {lines.map((line, idx) => (
          <li key={idx} className="text-lg leading-relaxed">
            {interpolate(line, variableValues, accent)}
          </li>
        ))}
      </Tag>
    )
  }

  const rendered = interpolate(content, variableValues, accent)

  const CALLOUT_CONFIG = {
    'callout-info': { icon: Info, bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },
    'callout-warning': { icon: AlertTriangle, bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
    'callout-success': { icon: CheckCircle, bg: '#f0fdf4', border: '#22c55e', text: '#166534' },
    'callout-error': { icon: XCircle, bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  } as const

  if (style in CALLOUT_CONFIG) {
    const cfg = CALLOUT_CONFIG[style as keyof typeof CALLOUT_CONFIG]
    const Icon = cfg.icon
    return (
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-xl"
        style={{ backgroundColor: cfg.bg, borderLeft: `4px solid ${cfg.border}` }}
      >
        <Icon className="h-5 w-5 shrink-0 mt-0.5" style={{ color: cfg.border }} />
        <p className="text-sm leading-relaxed" style={{ color: cfg.text }}>{rendered}</p>
      </div>
    )
  }

  switch (style) {
    case 'h1':
      return <h1 className="text-3xl font-bold leading-tight" style={baseStyle}>{rendered}</h1>
    case 'h2':
      return <h2 className="text-2xl font-bold leading-tight" style={baseStyle}>{rendered}</h2>
    case 'h3':
      return <h3 className="text-xl font-semibold leading-snug" style={baseStyle}>{rendered}</h3>
    case 'bold':
      return <p className="text-lg font-bold leading-relaxed" style={baseStyle}>{rendered}</p>
    case 'quote':
      return (
        <blockquote
          className="text-lg italic leading-relaxed pl-4"
          style={{ ...baseStyle, borderLeft: `3px solid ${accent}`, color: '#64748b' }}
        >
          {rendered}
        </blockquote>
      )
    case 'highlight':
      return (
        <p className="text-lg leading-relaxed">
          <mark
            className="px-1 rounded"
            style={{
              backgroundColor: `color-mix(in srgb, ${accent} 15%, transparent)`,
              color: baseStyle.color,
              textAlign: baseStyle.textAlign,
            }}
          >
            {rendered}
          </mark>
        </p>
      )
    default:
      return <p className="text-lg leading-relaxed" style={baseStyle}>{rendered}</p>
  }
}
